"use client";

import { useSyncExternalStore, type MouseEvent } from "react";
import type { Locale } from "@/lib/i18n";
import {
  buildTrackedStoreUrl,
  trackStoreClick,
} from "@/lib/analytics-events";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/store-links";

// intent:// 를 조용히 무시하는 브라우저(일부 인앱 웹뷰 등)용 폴백 대기 시간.
// 앱 전환이 일어나면 페이지가 hidden 으로 바뀌므로 폴백을 건너뛴다.
const INTENT_FALLBACK_DELAY_MS = 1500;

const noopSubscribe = () => () => undefined;

const GOOGLE_PLAY_BADGES = {
  ko: {
    webpSrc: "/store-badges/google-play-badge-ko.webp",
    pngSrc: "/store-badges/google-play-badge-ko.png",
    alt: "Google Play에서 다운로드",
  },
  en: {
    webpSrc: "/store-badges/google-play-badge-en.webp",
    pngSrc: "/store-badges/google-play-badge-en.png",
    alt: "Get it on Google Play",
  },
} as const;

const APP_STORE_BADGE = {
  webpSrc: "/store-badges/app-store-badge-en.webp",
  pngSrc: "/store-badges/app-store-badge-en.png",
  width: 360,
  height: 120,
} as const;

type StoreBadgeImageProps = {
  webpSrc: string;
  pngSrc: string;
  alt: string;
  width: number;
  height: number;
  className: string;
  priority?: boolean;
};

function isIosDevice(): boolean {
  const { maxTouchPoints, platform, userAgent } = window.navigator;

  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1)
  );
}

function isAndroidDevice(): boolean {
  return /Android/i.test(window.navigator.userAgent);
}

function getCanShowGooglePlay(): boolean {
  if (typeof window === "undefined") return false;
  return !isIosDevice();
}

function buildAndroidIntentUrl(fallbackUrl: string): string {
  // Android intent URI — 앱이 설치돼 있으면 modutest:// 딥링크로 바로 열고,
  // 없으면 browser_fallback_url 로 플레이스토어에 보낸다(Chromium 계열 내장 동작).
  // authority 를 비워 modutest:// 가 되게 한다 — 앱 딥링크 핸들러가 빈 host·path 를
  // 홈('/')으로 라우팅한다(modutest://home 은 /home 404 가 되므로 쓰지 말 것).
  return `intent://#Intent;scheme=modutest;package=com.simplesoft.modutest;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}

// Android 에서만 클릭을 가로채 intent URI 로 이동 — 앱 설치 시 바로 앱이 열린다.
// 데스크톱 등 나머지 환경은 기본 동작(새 탭으로 플레이스토어)을 유지한다.
function handleGooglePlayClick(event: MouseEvent<HTMLAnchorElement>) {
  trackStoreClick("google_play", event.currentTarget.dataset.locale ?? "ko");
  if (!isAndroidDevice()) return;
  event.preventDefault();

  const fallbackUrl = event.currentTarget.href;
  // intent:// 를 조용히 무시하는 브라우저용 폴백. 앱 전환·스토어 이동 등으로
  // 페이지를 떠나기 시작하면(pagehide) 취소해 이중 내비게이션을 막는다.
  const fallbackTimer = window.setTimeout(() => {
    if (!document.hidden) window.location.assign(fallbackUrl);
  }, INTENT_FALLBACK_DELAY_MS);
  window.addEventListener(
    "pagehide",
    () => window.clearTimeout(fallbackTimer),
    { once: true },
  );

  window.location.href = buildAndroidIntentUrl(fallbackUrl);
}

function getServerCanShowGooglePlay(): boolean {
  return true;
}

function StoreBadgeImage({
  webpSrc,
  pngSrc,
  alt,
  width,
  height,
  className,
  priority = false,
}: StoreBadgeImageProps) {
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={pngSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className={className}
      />
    </picture>
  );
}

export default function StoreBadges({ locale }: { locale: Locale }) {
  const canShowGooglePlay = useSyncExternalStore(
    noopSubscribe,
    getCanShowGooglePlay,
    getServerCanShowGooglePlay,
  );
  const googlePlayBadge = GOOGLE_PLAY_BADGES[locale];
  const googlePlayUrl = buildTrackedStoreUrl(GOOGLE_PLAY_URL, "google_play", locale);
  const appStoreUrl = buildTrackedStoreUrl(APP_STORE_URL, "app_store", locale);
  const appStoreAlt =
    locale === "ko" ? "App Store에서 다운로드" : "Download on the App Store";

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick("app_store", locale)}
        aria-label={appStoreAlt}
        className="inline-flex h-10 items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
      >
        <StoreBadgeImage
          webpSrc={APP_STORE_BADGE.webpSrc}
          pngSrc={APP_STORE_BADGE.pngSrc}
          alt={appStoreAlt}
          width={APP_STORE_BADGE.width}
          height={APP_STORE_BADGE.height}
          className="h-10 w-auto"
          priority={false}
        />
      </a>

      {canShowGooglePlay && (
        <a
          href={googlePlayUrl}
          data-locale={locale}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleGooglePlayClick}
          aria-label={googlePlayBadge.alt}
          className="inline-flex h-[52px] items-center transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        >
          <StoreBadgeImage
            webpSrc={googlePlayBadge.webpSrc}
            pngSrc={googlePlayBadge.pngSrc}
            alt={googlePlayBadge.alt}
            width={646}
            height={250}
            className="w-[135px] max-w-full h-auto"
            priority
          />
        </a>
      )}
    </div>
  );
}
